'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Loader2, Check } from 'lucide-react';
import { aiAssistantApi } from '@/lib/api/ai-assistant';
import { PageState } from '@/components/common/feedback/PageState';

interface Config {
  key: string;
  value: string;
  description?: string;
}

/**
 * 系统配置页面
 */
export default function ConfigPage() {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState<Config[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [savedKey, setSavedKey] = useState<string | null>(null);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    setIsLoading(true);
    try {
      const response: any = await aiAssistantApi.getConfigs();
      // Transform the response to config array
      const configData = response.data || {};
      const configArray: Config[] = Object.entries(configData).map(([key, value]) => ({
        key,
        value: String(value),
        description: getConfigDescription(key),
      }));
      setConfigs(configArray);
    } catch (err) {
      console.error('Failed to load configs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfigDescription = (key: string): string => {
    return t.aiAssistant.config.descriptions[key as keyof typeof t.aiAssistant.config.descriptions] || '';
  };

  const handleEdit = (config: Config) => {
    setEditingKey(config.key);
    setEditValue(config.value);
  };

  const handleSave = async (key: string) => {
    setIsSaving(true);
    try {
      await aiAssistantApi.updateConfig(key, editValue);
      setConfigs(prev => prev.map(c => 
        c.key === key ? { ...c, value: editValue } : c
      ));
      setEditingKey(null);
      setSavedKey(key);
      setTimeout(() => setSavedKey(null), 2000);
    } catch (err) {
      console.error('Failed to save config:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValue('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* 页面标题 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {t.aiAssistant.config.title}
        </h1>
        <p className="text-gray-500 mt-1">
          {t.aiAssistant.config.pageDescription}
        </p>
      </div>

      {/* 配置列表 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {configs.length === 0 ? (
          <div className="py-12">
            <PageState
              variant="empty"
              title={t.aiAssistant.config.emptyTitle}
              description={t.aiAssistant.config.emptyDesc}
              layout="center"
              className="mx-auto w-full max-w-2xl border-0 bg-transparent"
            />
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t.aiAssistant.config.key}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t.aiAssistant.config.value}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t.aiAssistant.config.description}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t.aiAssistant.config.actions}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {configs.map((config) => (
                <tr key={config.key} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <code className="text-sm text-purple-600 bg-purple-50 px-2 py-1 rounded">
                      {config.key}
                    </code>
                  </td>
                  <td className="px-6 py-4">
                    {editingKey === config.key ? (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-full px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                        autoFocus
                      />
                    ) : (
                      <span className="text-sm text-gray-900">{config.value}</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-500">{config.description}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {editingKey === config.key ? (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={handleCancel}
                          className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                          {t.aiAssistant.config.cancel}
                        </button>
                        <button
                          onClick={() => handleSave(config.key)}
                          disabled={isSaving}
                          className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                        >
                          {isSaving && <Loader2 className="w-3 h-3 animate-spin" />}
                          {t.aiAssistant.config.save}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        {savedKey === config.key && (
                          <span className="flex items-center gap-1 text-green-600 text-sm">
                            <Check className="w-4 h-4" />
                            {t.aiAssistant.config.saved}
                          </span>
                        )}
                        <button
                          onClick={() => handleEdit(config)}
                          className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                          {t.aiAssistant.config.edit}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
